import { getReq, postReq, putReq, deleteReq, patchReq } from './api';
import { OfficialData, OfficialSummary } from './officialsService';

export interface OperationLocation {
  id: string;
  name: string;
  street_address_1?: string;
  street_address_2?: string;
  city?: string;
  state?: string;
  zip?: string;
  primary: boolean;
}

export type OperationCheckInState = 'checked_in' | 'checked_out';

export type OperationRoleName = 'Command Staff' | 'Incident Commander' | 'Deputy Incident Commander' | 'Participant';

export interface OperationAgencyOfficial {
  id: string;
  agency_official: OfficialData;
  assignment: string;
  active?: boolean;
  operation_role: {
    id: string;
    name: OperationRoleName;
    description: string;
  };
  operation_team_members: {
    id: string;
    operation_team_id: string;
    effective_from: string;
    effective_to: null;
    active: boolean;
  }[];
  operation_check_in: {
    state: OperationCheckInState
  },
  operation_id: string
}
export interface Operation {
  id: string;
  title: string;
  agency_assigned_identifier: string;
  description: string;
  is_planned: boolean;
  is_emergency: boolean;
  start_date: string;
  end_date?: string;
  status: 'planning' | 'active' | 'closed' | 'archived';
  agency_id: string;
  operation_locations: OperationLocation[];
  operation_agency_officials: OperationAgencyOfficial[];
  operation_teams: OperationTeam[];
}

export interface CreateOperationData {
  title: string;
  agency_assigned_identifier: string;
  description: string;
  is_planned: boolean;
  is_emergency: boolean;
  start_date: string;
  end_date?: string;
  status: 'planning' | 'active' | 'closed' | 'archived';
  operation_locations_attributes: Omit<OperationLocation, 'id'>[];
  operation_agency_officials_attributes?: {
    agency_official_id: number;
    assignment: string;
    operation_role_id: string;
    team_assignments?: {
      team_id: string;
    }[];
  }[];
  operation_teams_attributes?: { name: string }[]
}

export interface UpdateOperationData {
  title?: string;
  agency_assigned_identifier?: string;
  description?: string;
  is_planned?: boolean;
  is_emergency?: boolean;
  start_date?: string;
  end_date?: string;
  status?: 'planning' | 'active' | 'closed' | 'archived';
  operation_locations_attributes?: Omit<OperationLocation, 'id'>[];
  operation_agency_officials_attributes?: UpdateOperationOfficialData[];
}

export interface UpdateOperationOfficialData {
  agency_official_id: number;
  assignment?: string;
  operation_role_id?: string;
  team_assignments?: {
    team_id: string;
  }[];
  _destroy?: true;
  active?: boolean
}

export interface CreateOperationCheckInData {
  operation_agency_official_id: string;
  state: OperationCheckInState;
}

export interface OperationRole {
  id: string;
  name: OperationRoleName;
  description: string;
}

export interface OperationTeam {
  id: string;
  name: string;
}

export interface OperationActivity {
  id: string;
  description: string;
  author: {
    agency_official: OfficialSummary
  };
  performed_at: string;
  incident_id: string | null;
  officials: {
    id: string;
    agency_official: OfficialSummary;
  }[]
}

export interface OperationActivitySummary {
  id: string;
  description: string;
  performed_at: string;
  active: boolean;
  author: {
    id: string;
    agency_official: OfficialSummary;
  };
};

export interface OperationSummary {
  id: string;
  title: string;
}

export interface OperationIncident {
  id: string;
  activity: OperationActivitySummary | null;
  title: string;
  case_number: string | null;
  incident_status: string;
  incident_type: string;
  performed_at: string;
  officials: {
    id: string;
    agency_official: OfficialSummary
  }[];
  location: {
    id: string;
    name: string;
    street_address_1: string;
    street_address_2: string | null;
    city: string;
    state: string;
    zip: string;
  } | null;
  author: {
    id: string;
    agency_official: OfficialSummary;
  };
}

export interface OperationIncidentNote {
  id: string;
  description: string;
  performed_at: string;
  author: {
    id: string;
    agency_official: OfficialSummary;
  };
};

export const fetchAgencyOperations = async (
  accessToken: string,
  agencyId: number | string
): Promise<{ data?: Operation[]; error?: string }> => {
  return getReq<Operation[]>(
    `/agencies/${agencyId}/operations`,
    accessToken,
    'Failed to fetch operations.'
  );
};

export const getOperation = async (
  accessToken: string,
  operationId: string
): Promise<{ data?: Operation; error?: string }> => {
  return getReq<Operation>(`/operations/${operationId}`, accessToken, 'Failed to fetch operation.');
};

export const getOperationSummary = async (
  accessToken: string,
  operationId: string
): Promise<{ data?: OperationSummary; error?: string }> => {
  return getReq<OperationSummary>(`/operations/${operationId}/summary`, accessToken, 'Failed to fetch operation.');
};

export const createAgencyOperation = async (
  accessToken: string,
  agencyId: number | string,
  operationData: CreateOperationData
): Promise<{ data?: Operation; error?: string }> => {
  return postReq<Operation>(
    `/agencies/${agencyId}/operations`,
    accessToken,
    { operation: operationData },
    'Failed to create operation.'
  );
};

export const updateOperation = async (
  accessToken: string,
  operationId: string,
  operationData: UpdateOperationData,
): Promise<{ data?: Operation; error?: string }> => {
  return putReq<Operation>(
    `/operations/${operationId}`,
    accessToken,
    { operation: operationData },
    'Failed to update operation.'
  );
};

export const updateOperationOfficials = async ({
  accessToken,
  operationId,
  operationOfficialData,
}: {
  accessToken: string;
  operationId: string;
  operationOfficialData: UpdateOperationOfficialData[];
}
): Promise<{ data?: Operation; error?: string }> => {
  return patchReq<Operation>(
    `/operations/${operationId}/update_officials`,
    accessToken,
    { operation_agency_officials: operationOfficialData },
    'Failed to update operation.'
  );
};

export const deleteOperation = async (
  accessToken: string,
  operationId: string
): Promise<{ error?: string }> => {
  return deleteReq(`/operations/${operationId}`, accessToken, 'Failed to delete operation.');
};

export const duplicateOperation = async (
  accessToken: string,
  operationId: string,
): Promise<{ data?: Operation; error?: string }> => {
  return postReq<Operation>(
    `/operations/${operationId}/duplicate`,
    accessToken,
    'Failed to duplicate operation.'
  );
};

export const fetchOperationRoles = async (
  accessToken: string
): Promise<{ data?: OperationRole[]; error?: string }> => {
  return getReq<OperationRole[]>(
    `/operation_roles`,
    accessToken,
    'Failed to fetch operation roles.'
  );
};

export const createOperationCheckIn = async (
  accessToken: string,
  operationId: string,
  operationCheckInData: CreateOperationCheckInData,
): Promise<{ data?: Operation; error?: string }> => {
  return postReq<Operation>(
    `/operations/${operationId}/operation_check_ins`,
    accessToken,
    { operation_check_in: operationCheckInData },
    'Failed to create operation checkin'
  );
};

export const getOperationActivities = async ({ accessToken, operationId }: {
  accessToken: string;
  operationId: string;
}): Promise<{ data?: OperationActivity[], error?: string }> => {
  return getReq<OperationActivity[]>(
    `/operations/${operationId}/operation_activities`,
    accessToken,
    'Failed to fetch operation activities'
  );
};

export const getOperationActivity = async ({ accessToken, activityId }): Promise<{
  data?: OperationActivity;
  error?: string
}> => {
  return getReq<OperationActivity>(
    `/operation_activities/${activityId}`,
    accessToken,
    'Failed to fetch activity'
  );
};

export const createOperationActivity = async ({ accessToken, operationId, params }) => {
  return postReq<OperationActivity>(
    `/operations/${operationId}/operation_activities`,
    accessToken,
    { operation_activity: params },
    'Failed to to create activity'
  );
};

export const updateOperationActivity = async ({ accessToken, activityId, payload }: {
  accessToken?: string;
  activityId: string;
  payload: {
    description?: string;
    performed_at?: string;
    official_ids?: string[];
    incident_id?: string;
  },
}): Promise<{ data?: OperationActivity; error?: string }> => {
  if (!accessToken) {
    throw new Error('Access token is required');
  }
  return putReq(
    `/operation_activities/${activityId}`,
    accessToken,
    { operation_activity: payload },
    'Failed to to update activity'
  );
};

export const deleteOperationActivity = async ({ accessToken, activityId }): Promise<{ error?: string }> => {
  return deleteReq(
    `/operation_activities/${activityId}`, accessToken, 'Failed to to delete activity'
  );
};

export const getOperationIncidents = async ({ accessToken, operationId, orderBy }: {
  accessToken: string;
  operationId: string;
  orderBy: string;
}): Promise<{ data?: OperationIncident[], error?: string }> => {
  return getReq<OperationIncident[]>(
    `/operations/${operationId}/operation_incidents?order_by=${orderBy}`,
    accessToken,
    'Failed to fetch operation incidents'
  );
};

export const getOperationIncident = async ({ accessToken, incidentId }): Promise<{
  data?: OperationIncident;
  error?: string
}> => {
  return getReq<OperationIncident>(
    `/operation_incidents/${incidentId}`,
    accessToken,
    'Failed to fetch incident'
  );
};

export const getOperationIncidentNotes = async ({ accessToken, incidentId }: {
  accessToken: string;
  incidentId: string;
}): Promise<{ data?: OperationIncidentNote[], error?: string }> => {
  return getReq<OperationIncidentNote[]>(
    `/operation_incidents/${incidentId}/operation_incident_notes`,
    accessToken,
    'Failed to fetch operation incident notes'
  );
};

export const createOperationIncidentNote = async ({ accessToken, incidentId, params }: {
  accessToken: string;
  incidentId: string;
  params: { description: string };
}): Promise<{ data?: OperationIncidentNote, error?: string }> => {
  return postReq<OperationIncidentNote>(
    `/operation_incidents/${incidentId}/operation_incident_notes`,
    accessToken,
    { operation_incident_note: params },
    'Failed to create operation incident note'
  );
};

export const editOperationIncidentNote = async ({ accessToken, noteId, params }: {
  accessToken: string;
  noteId: string;
  params: { description: string, performed_at: string };
}): Promise<{ data?: OperationIncidentNote, error?: string }> => {
  return putReq<OperationIncidentNote>(
    `/operation_incident_notes/${noteId}`,
    accessToken,
    { operation_incident_note: params },
    'Failed to edit operation incident note'
  );
};

export const deleteOperationIncidentNote = async ({ accessToken, noteId }: {
  accessToken: string;
  noteId: string;
}): Promise<{ error?: string }> => {
  return deleteReq(
    `/operation_incident_notes/${noteId}`, accessToken, 'Failed to delete operation incident note'
  );
};

export const updateOperationIncident = async ({
  accessToken,
  incidentId,
  payload
}: {
  accessToken: string;
  incidentId: string;
  payload: {
    case_number?: string;
    title?: string;
    incident_status?: string;
    performed_at?: string;
    incident_type?: string;
    location_attributes?: {
      name: string;
      street_address_1: string;
      street_address_2: string | null;
      city: string;
      state: string;
      zip: string;
    };
    official_ids?: string[];
  };
}): Promise<{
  data?: OperationIncident;
  error?: string;
}> => {
  return patchReq<OperationIncident>(
    `/operation_incidents/${incidentId}`,
    accessToken,
    { operation_incident: payload },
    'Failed to update incident'
  );
};

export const createOperationIncident = async ({ accessToken, operationId, params }: {
  accessToken: string;
  operationId: string;
  params: {
    title: string;
    case_number?: string;
    incident_type: string;
    performed_at: string;
    official_ids?: string[];
    activity_id?: string;
  };
}): Promise<{ data?: OperationIncident; error?: string }> => {
  return postReq<OperationIncident>(
    `/operations/${operationId}/operation_incidents`,
    accessToken,
    { operation_incident: params },
    'Failed to create incident'
  );
};

type OpOfficial = {
  id: string
  agency_official: {
    id: number
    first_name: string
    last_name: string
    img_url: string | null
  }
};
type OperationTeamSearchResult = {
  kind: 'operation_team'
  team: {
    id: string
    name: string
    officials: OpOfficial[]
  }
};
type OperationOfficialSearchResult = {
  kind: 'operation_agency_official'
  official: OpOfficial
};
export type OperationSearchResult = OperationTeamSearchResult | OperationOfficialSearchResult;

export const searchOperationAgencyOfficials = async ({ accessToken, operationId, search }: {
  accessToken: string;
  operationId: string;
  search: string;
}) => {
  const params = new URLSearchParams();
  params.append('q', search);
  return getReq<OperationSearchResult[]>(
    `/operations/${operationId}/search?${params.toString()}`,
    accessToken,
    'Failed to search operation'
  );
};
